import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Badge,
} from "@mui/material";
import { useTheme } from "../../contexts/ThemeContext";
import {
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Assignment as AssignmentIcon,
  People as PeopleIcon,
  ExitToApp as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CheckCircle as ApprovalIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  AccountBalance as GestaoIcon,
  Store as StoreIcon,
} from "@mui/icons-material";
import logoPortal from "../../assets/images/lupa.png";
import miniLupa from "../../assets/images/mini-lupa.png";
import { buscarCandidaturasPendentes } from "../../services/vaga";

interface SidebarProps {
  onItemClick: () => void;
  compactMode?: boolean;
  setCompactMode?: (value: boolean) => void;
}

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  divider?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({
  onItemClick,
  compactMode = false,
  setCompactMode,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const [aprovacoesPendentes, setAprovacoesPendentes] = useState(0);

  // Detectar se é área da empresa ou prefeitura
  const isEmpresaArea =
    location.pathname.includes("/dashboard-empresa") ||
    location.pathname.includes("/aprovacoes") ||
    location.pathname.includes("/cadastrar-vaga") ||
    location.pathname.includes("/gerenciar-vagas") ||
    location.pathname.includes("/candidatos-aprovados") ||
    location.pathname.includes("/configuracoes-empresa");

  // Função para buscar aprovações pendentes
  const buscarAprovacoesPendentes = async () => {
    if (!isEmpresaArea) return;

    try {
      const pendentes = await buscarCandidaturasPendentes();
      setAprovacoesPendentes(pendentes.data.length);
    } catch (error: any) {
      // Log específico para diferentes tipos de erro
      if (error?.response?.status === 500) {
        console.warn(
          "Servidor indisponível (500) - mantendo último valor de aprovações pendentes",
        );
      } else if (error?.response?.status === 503) {
        console.warn(
          "Serviço indisponível (503) - mantendo último valor de aprovações pendentes",
        );
      } else if (error?.code === "ECONNABORTED") {
        console.warn("Timeout na busca de aprovações pendentes");
      } else {
        console.error("Erro ao buscar aprovações pendentes:", error?.message);
      }
      // Não resetar para 0 se for erro de servidor - manter valor anterior
      // setAprovacoesPendentes(0);
    }
  };

  // Effect para buscar aprovações pendentes quando estiver na área da empresa
  useEffect(() => {
    if (isEmpresaArea) {
      buscarAprovacoesPendentes();

      // Listener para atualizações de candidaturas
      const handleCandidaturaProcessada = () => {
        buscarAprovacoesPendentes();
      };

      window.addEventListener(
        "candidaturaProcessada",
        handleCandidaturaProcessada,
      );

      // Atualizar a cada 5 minutos para reduzir carga no servidor
      const interval = setInterval(buscarAprovacoesPendentes, 300000);

      return () => {
        clearInterval(interval);
        window.removeEventListener(
          "candidaturaProcessada",
          handleCandidaturaProcessada,
        );
      };
    }
  }, [isEmpresaArea]);

  // Menu para área da empresa
  const menuItemsEmpresa: MenuItem[] = [
    {
      text: "Dashboard",
      icon: <DashboardIcon />,
      path: "/dashboard-empresa",
    },
    {
      text: "Aprovações",
      icon: <ApprovalIcon />,
      path: "/aprovacoes",
    },
    {
      text: "Cadastrar Vaga",
      icon: <AddIcon />,
      path: "/cadastrar-vaga",
    },
    {
      text: "Gerenciar Vagas",
      icon: <AssignmentIcon />,
      path: "/gerenciar-vagas",
    },
    {
      text: "Gerenciar Candidatos",
      icon: <PeopleIcon />,
      path: "/candidatos-aprovados",
    },
  ];

  // Menu original para área da prefeitura
  const menuItemsPrefeitura: MenuItem[] = [
    {
      text: "Dashboard",
      icon: <DashboardIcon />,
      path: "/dashboard-prefeitura",
    },
    {
      text: "Painel de Vagas",
      icon: <WorkIcon />,
      path: "/listar-vagas-prefeitura",
    },
    {
      text: "Exibir Empresas",
      icon: <BusinessIcon />,
      path: "/listar-empresas",
    },
    {
      text: "Exibir Candidatos",
      icon: <PeopleIcon />,
      path: "/listar-candidatos",
    },
    {
      text: "Empresas Permitidas",
      icon: <StoreIcon />,
      path: "/listar-empresas-permitidas",
    },
    {
      text: "Gestão Prefeitura",
      icon: <GestaoIcon />,
      path: "/gestao-prefeitura",
    },
  ];

  // Escolher qual menu usar
  const menuItems = isEmpresaArea ? menuItemsEmpresa : menuItemsPrefeitura;

  const handleNavigation = (path: string) => {
    navigate(path);
    onItemClick();
  };

  const handleLogout = async () => {
    // Use the context logout function
    logout();

    // Navigate to appropriate page
    if (isEmpresaArea) {
      navigate("/login-empresa?logout=true");
    } else {
      navigate("/");
    }

    onItemClick();
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: compactMode ? 2 : 3,
          textAlign: "center",
          backgroundColor: "#332F30",
          color: "white",
          position: "relative",
          borderRadius: "0 20px 0 0",
          minHeight: compactMode ? "60px" : "120px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {!compactMode ? (
          <>
            <Box
              component="img"
              src={logoPortal}
              alt="Portal de Vagas"
              sx={{
                width: "100%",
                maxWidth: "200px",
                height: "auto",
                maxHeight: "80px",
                objectFit: "contain",
                objectPosition: "center",
              }}
            />
            <Typography
              variant="body1"
              sx={{
                mt: 1.5,
                opacity: 0.85,
                fontSize: "0.95rem",
                color: "rgba(255, 255, 255, 0.8)",
                letterSpacing: "0.5px",
              }}
            >
              Sistema de Gestão
            </Typography>
          </>
        ) : (
          <Box
            sx={{
              py: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              component="img"
              src={miniLupa}
              alt="Mini Lupa"
              sx={{
                width: 30,
                height: 30,
                objectFit: "contain",
              }}
            />
          </Box>
        )}
      </Box>

      <Divider
        sx={{
          borderColor: "rgba(255, 255, 255, 0.12)",
          mx: 2,
        }}
      />

      {/* Menu Items */}
      <List sx={{ flexGrow: 1, pt: 2 }}>
        {menuItems.map((item, index) => (
          <React.Fragment key={index}>
            <ListItem disablePadding>
              <Tooltip
                title={compactMode ? item.text : ""}
                placement="right"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: "rgba(0, 0, 0, 0.9)",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      py: 1,
                      px: 1.5,
                      maxWidth: 200,
                      "& .MuiTooltip-arrow": {
                        color: "rgba(0, 0, 0, 0.9)",
                      },
                    },
                  },
                }}
                enterDelay={300}
                leaveDelay={0}
              >
                <ListItemButton
                  onClick={() => handleNavigation(item.path!)}
                  selected={location.pathname === item.path}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    mb: 0.5,
                    color: "rgba(255, 255, 255, 0.9)",
                    transition: "all 0.2s ease-in-out",
                    justifyContent: compactMode ? "center" : "flex-start",
                    "&.Mui-selected": {
                      backgroundColor: "rgba(255, 255, 255, 0.08)",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.16)",
                        transform: "scale(1.05)",
                      },
                    },
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.04)",
                      transform: "scale(1.05)",
                      "& .MuiListItemIcon-root": {
                        transform: "scale(1.1)",
                        transition: "transform 0.2s ease-in-out",
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: "rgba(255, 255, 255, 0.7)",
                      minWidth: compactMode ? 0 : 45,
                      transition: "transform 0.2s ease-in-out",
                      mx: compactMode ? "auto" : 0,
                    }}
                  >
                    {item.text === "Aprovações" && aprovacoesPendentes > 0 ? (
                      <Badge
                        badgeContent={
                          aprovacoesPendentes > 99 ? "99+" : aprovacoesPendentes
                        }
                        color="error"
                        sx={{
                          "& .MuiBadge-badge": {
                            fontSize: "0.75rem",
                            minWidth: 18,
                            height: 18,
                            backgroundColor: "#ff4444",
                            color: "white",
                          },
                        }}
                      >
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>
                  {!compactMode && (
                    <ListItemText
                      primary={item.text}
                      sx={{
                        color: "inherit",
                        "& .MuiListItemText-primary": {
                          fontSize: "1.05rem",
                          fontWeight:
                            location.pathname === item.path ? 600 : 400,
                        },
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            {item.divider && (
              <Divider
                sx={{ my: 1, borderColor: "rgba(255, 255, 255, 0.12)" }}
              />
            )}
          </React.Fragment>
        ))}
      </List>

      {/* Spacer para empurrar o logout para baixo */}
      <Box sx={{ flexGrow: 1 }} />

      <Divider
        sx={{
          borderColor: "rgba(255, 255, 255, 0.12)",
          mx: 2,
        }}
      />

      {/* Configurações e Logout */}
      <List sx={{ pb: 2, pt: 1 }}>
        {/* Configurações - para empresa e prefeitura */}
        <ListItem disablePadding>
          <Tooltip
            title={compactMode ? "Configurações" : ""}
            placement="right"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: "rgba(0, 0, 0, 0.9)",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  py: 1,
                  px: 1.5,
                  maxWidth: 200,
                  "& .MuiTooltip-arrow": {
                    color: "rgba(0, 0, 0, 0.9)",
                  },
                },
              },
            }}
            enterDelay={300}
            leaveDelay={0}
          >
            <ListItemButton
              onClick={() => handleNavigation(
                isEmpresaArea ? "/configuracoes-empresa" : "/configuracoes-prefeitura"
              )}
              selected={
                location.pathname === "/configuracoes-empresa" || 
                location.pathname === "/configuracoes-prefeitura"
              }
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  mb: 1,
                  color: "rgba(255, 255, 255, 0.9)",
                  transition: "all 0.2s ease-in-out",
                  justifyContent: compactMode ? "center" : "flex-start",
                  "&.Mui-selected": {
                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.16)",
                      transform: "scale(1.05)",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.04)",
                    transform: "scale(1.05)",
                    "& .MuiListItemIcon-root": {
                      transform: "scale(1.1)",
                      transition: "transform 0.2s ease-in-out",
                    },
                  },
                }}
              >
              <ListItemIcon
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  minWidth: compactMode ? 0 : 45,
                  mx: compactMode ? "auto" : 0,
                  transition: "transform 0.2s ease-in-out",
                }}
              >
                <SettingsIcon />
              </ListItemIcon>
              {!compactMode && (
                <ListItemText
                  primary="Configurações"
                  sx={{
                    color: "inherit",
                    "& .MuiListItemText-primary": {
                      fontSize: "1.05rem",
                      fontWeight:
                        location.pathname === "/configuracoes-empresa" ||
                        location.pathname === "/configuracoes-prefeitura"
                          ? 600
                          : 400,
                    },
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>

        {/* Logout */}
        <ListItem disablePadding>
          <Tooltip
            title={compactMode ? "Sair" : ""}
            placement="right"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: "rgba(0, 0, 0, 0.9)",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  py: 1,
                  px: 1.5,
                  maxWidth: 200,
                  "& .MuiTooltip-arrow": {
                    color: "rgba(0, 0, 0, 0.9)",
                  },
                },
              },
            }}
            enterDelay={300}
            leaveDelay={0}
          >
            <ListItemButton
              onClick={handleLogout}
              sx={{
                mx: 1,
                borderRadius: 1,
                mb: 1,
                color: "#333",
                transition: "all 0.2s ease-in-out",
                justifyContent: compactMode ? "center" : "flex-start",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.04)",
                  transform: "scale(1.05)",
                  "& .MuiListItemIcon-root": {
                    color: "#ff6b6b",
                    transform: "scale(1.1)",
                    transition: "all 0.2s ease-in-out",
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: "white",
                  minWidth: compactMode ? 0 : 45,
                  mx: compactMode ? "auto" : 0,
                }}
              >
                <LogoutIcon />
              </ListItemIcon>
              {!compactMode && (
                <ListItemText
                  primary="Sair"
                  sx={{
                    color: "rgba(255, 255, 255, 0.9)",
                    "& .MuiListItemText-primary": {
                      fontSize: "1.05rem",
                      fontWeight: 400,
                    },
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </List>

      {/* Botão de toggle no meio da sidebar */}
      {setCompactMode && (
        <IconButton
          onClick={() => setCompactMode(!compactMode)}
          sx={{
            position: "absolute",
            right: -20,
            top: "50%",
            transform: "translateY(-50%)",
            backgroundColor: "#332F30",
            color: "white",
            width: 40,
            height: 40,
            border: "2px solid rgba(255, 255, 255, 0.1)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
            "&:hover": {
              backgroundColor: "#3a3a3a",
              border: "2px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
            },
            zIndex: 1,
          }}
        >
          {compactMode ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      )}
    </Box>
  );
};

export default Sidebar;
